import { ref } from "vue";
import api from "@/api";
import router from "@/router";
import { getStoredAccessData, hasValidSession } from "@/publicjs/authSession";
import { openAccessPermissionStream } from "@/utils/accessPermissionStream";

const MODULES_STORAGE_KEY = "modules";
export const MODULES_UPDATED_EVENT = "erp-modules-updated";

export const modulesState = ref([]);
export const modulesLoadingState = ref(true);
export const modulesErrorState = ref("");

let stopAccessModulesStream = null;
let activeStreamUserId = null;
let validatedSession = "";
let refreshVersion = 0;
let pendingRefresh = null;
let pendingSession = "";

const sessionKey = () => {
  const access = getStoredAccessData();
  return hasValidSession() ? `${access?.user?.key || 0}:${access?.token || ""}` : "";
};

const normalizeModules = (modules) => (Array.isArray(modules) ? modules : []);

export const shouldUseRealtimeAccessModules = () => {
  try {
    const apiUrl = new URL(process.env.VUE_APP_API_URL);
    return !["localhost", "127.0.0.1"].includes(apiUrl.hostname);
  } catch (error) {
    return process.env.NODE_ENV === "production";
  }
};

const emitModulesUpdated = (modules) => {
  window.dispatchEvent(
    new CustomEvent(MODULES_UPDATED_EVENT, {
      detail: {
        modules,
      },
    }),
  );
};

export const syncModulesState = (modules) => {
  const normalizedModules = normalizeModules(modules);

  validatedSession = sessionKey();
  modulesState.value = normalizedModules;
  localStorage.setItem(MODULES_STORAGE_KEY, JSON.stringify(normalizedModules));
  emitModulesUpdated(normalizedModules);

  return normalizedModules;
};

export const hydrateModulesState = () => {
  // Persisted menus are used by legacy form helpers, but are not proof of current access.
  // Navigation and search only display a server response validated for this session.
  if (!sessionKey() || validatedSession !== sessionKey()) modulesState.value = [];
  return modulesState.value;
};

export const hasRouteModuleAccess = (path, modules = modulesState.value) => {
  const normalizedPath = String(path || "")
    .split("?")[0]
    .trim();

  if (
    normalizedPath === "" ||
    normalizedPath === "/" ||
    normalizedPath === "/login" ||
    normalizedPath.startsWith("/seguimiento/") ||
    normalizedPath.startsWith("/taller/seguimiento/")
  ) {
    return true;
  }

  return normalizeModules(modules).some((module) =>
    Array.isArray(module?.submodulos)
      ? module.submodulos.some((submodule) => submodule?.url === normalizedPath)
      : false,
  );
};

export const ensureCurrentRouteModuleAccess = (
  modules = modulesState.value,
) => {
  const currentRoutePath =
    router.currentRoute.value?.path || window.location.pathname || "/";

  if (
    !hasValidSession() ||
    !currentRoutePath ||
    currentRoutePath === "/login"
  ) {
    return;
  }

  if (!hasRouteModuleAccess(currentRoutePath, modules)) {
    router.replace("/").catch(() => null);
  }
};

export const refreshMyModules = () => {
  const accessData = getStoredAccessData();
  const currentUserId = Number(accessData?.user?.key || 0);
  const session = sessionKey();

  if (!currentUserId || !session) {
    refreshVersion++;
    pendingRefresh = null;
    pendingSession = "";
    modulesLoadingState.value = false;
    modulesErrorState.value = "";
    return Promise.resolve(syncModulesState([]));
  }
  if (pendingRefresh && pendingSession === session) return pendingRefresh;

  const version = ++refreshVersion;
  modulesLoadingState.value = true;
  modulesErrorState.value = "";
  modulesState.value = [];
  emitModulesUpdated([]);
  pendingSession = session;
  pendingRefresh = (async () => {
    try {
      const response = await api.get(`mis_modulos/${currentUserId}`);
      if (version !== refreshVersion || sessionKey() !== session) return [];
      const modules = syncModulesState(response?.data || []);
      ensureCurrentRouteModuleAccess(modules);
      return modules;
    } catch (error) {
      if (version !== refreshVersion || sessionKey() !== session) return [];
      modulesErrorState.value = "No se pudieron cargar tus permisos. Intenta nuevamente.";
      return syncModulesState([]);
    } finally {
      if (version === refreshVersion) {
        modulesLoadingState.value = false;
        pendingRefresh = null;
        pendingSession = "";
      }
    }
  })();

  return pendingRefresh;
};

export const startAccessModulesStream = () => {
  const accessData = getStoredAccessData();
  const currentUserId = Number(accessData?.user?.key || 0);
  const session = sessionKey();

  if (
    !currentUserId ||
    !hasValidSession() ||
    !shouldUseRealtimeAccessModules()
  ) {
    return null;
  }

  if (stopAccessModulesStream && activeStreamUserId === currentUserId) {
    return stopAccessModulesStream;
  }

  if (stopAccessModulesStream) {
    stopAccessModulesStream();
  }

  activeStreamUserId = currentUserId;
  stopAccessModulesStream = openAccessPermissionStream({
    onSnapshot: (payload) => {
      if (sessionKey() !== session) return;
      const affectedUserIds = Array.isArray(payload?.meta?.affected_user_ids)
        ? payload.meta.affected_user_ids.map((value) => Number(value))
        : [];

      if (
        affectedUserIds.length > 0 &&
        !affectedUserIds.includes(currentUserId)
      ) {
        return;
      }

      const modules = syncModulesState(payload?.snapshot || []);
      modulesLoadingState.value = false;
      modulesErrorState.value = "";
      ensureCurrentRouteModuleAccess(modules);
    },
    onError: () => {
      modulesLoadingState.value = false;
    },
  });

  return stopAccessModulesStream;
};
